import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { SpreadsheetColumnFilter } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-region-view";
import { SECTION_LABEL_CLASSNAME } from "./constants";

interface ValueFilterEditorProps {
  values: string[];
  filter: SpreadsheetColumnFilter;
  onChange: (filter: SpreadsheetColumnFilter) => void;
}

const VALUE_SEARCH_THRESHOLD = 8;

const getValueLabel = (value: string) => (value === "" ? "(Blanks)" : value);

export const ValueFilterEditor = ({
  values,
  filter,
  onChange,
}: ValueFilterEditorProps) => {
  const [search, setSearch] = useState("");
  const searchQuery = search.trim().toLocaleLowerCase();
  const shownValues =
    searchQuery === ""
      ? values
      : values.filter((value) =>
          getValueLabel(value).toLocaleLowerCase().includes(searchQuery),
        );
  const selectedCount = filter.selectedValues?.length ?? values.length;

  const toggleValue = (value: string, checked: boolean) => {
    const selected = new Set(filter.selectedValues ?? values);
    if (checked) selected.add(value);
    else selected.delete(value);
    onChange({ ...filter, selectedValues: Array.from(selected) });
  };

  return (
    <>
      <Stack
        className="w-full"
        gap={4}
        direction="row"
        align="center"
        justify="between"
      >
        <span className={SECTION_LABEL_CLASSNAME}>Filter by value</span>
        <Text variant="caption" tone="muted">
          {searchQuery === ""
            ? `${selectedCount} of ${values.length}`
            : `${shownValues.length} ${shownValues.length === 1 ? "match" : "matches"}`}
        </Text>
      </Stack>

      {values.length > VALUE_SEARCH_THRESHOLD && (
        <div className="w-full">
          <InputGroup>
            <InputGroupAddon align="inline-start">
              <MagnifyingGlassIcon />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search values"
              placeholder="Search values"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </InputGroup>
        </div>
      )}

      <Stack className="w-full" direction="row" align="center" gap={2}>
        <Button
          variant="ghost"
          size="xs"
          className="h-auto p-0"
          disabled={filter.selectedValues === null}
          onClick={() => onChange({ ...filter, selectedValues: null })}
        >
          Select all
        </Button>
        <span className="text-subtle">·</span>
        <Button
          variant="ghost"
          size="xs"
          className="h-auto p-0"
          disabled={selectedCount === 0}
          onClick={() => onChange({ ...filter, selectedValues: [] })}
        >
          Deselect all
        </Button>
      </Stack>

      <div className="quiet-scrollbar min-h-18 w-full flex-1 overflow-y-auto rounded-lg border border-line p-1">
        {shownValues.length === 0 ? (
          <Text variant="caption" tone="muted" className="px-2 py-1.5">
            No values match “{search}”.
          </Text>
        ) : (
          shownValues.map((value) => (
            <label
              key={value || "__blank__"}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-xs hover:bg-neutral-subtle-hovered"
            >
              <Checkbox
                checked={
                  filter.selectedValues === null ||
                  filter.selectedValues.includes(value)
                }
                onCheckedChange={(checked) =>
                  toggleValue(value, checked === true)
                }
              />
              <span
                className={cn("truncate", {
                  "text-subtle": value === "",
                })}
                title={getValueLabel(value)}
              >
                {getValueLabel(value)}
              </span>
            </label>
          ))
        )}
      </div>
    </>
  );
};
